import { ChangeEvent, useState, useEffect } from 'react';
import axios from 'axios';

import axiosInstance from '../../services/axios/axios';
import Edit from './containers/Edit';
import FormEntry from './components/FormEntry';
import UARLayout from './containers/Layout';
import {
	Headline5,
	Headline6,
} from '../../components/UI/MDC/Typography/Typography';
import { TextboxTypes, DropdownTypes } from '../../components/UI/Input';
import { WrapImsPacket } from '../../store/utility';
import * as actions from '../../store/ancillary/actions'
import * as utils from '../../utils';
import SubmissionSummary from './containers/SubmissionSummary';
import { SUBMISSION_LOADING_TITLE, UAR_CONSTANTS } from './util/constants';
import AddressForm from '../../components/Address/AddressForm';
import {
	ADDRESS_FORM_TYPE,
	getFormTemplate,
	isSelectedCountryUSOrDefault,
	isValidCountryCode,
	isValidZip,
} from '../../components/Address/utils';

const UpdateProfile = props => {
	/**
	 * Extract needed variables from the props provided by the `Scaffold`
	 */
	const {
		dispatch,
		loggedInUser,
		error,
		setError,
		errorMessage,
		loading,
		profileDetails,
		canPerformActions,
		esoFiId,
	} = props;

	//STATE

	/**
	 * (set)Submitted - flag to indicate if submit button has been clicked
	 */
	const [submitted, setSubmitted] = useState(false);

	/**
	 * (set)SubmittedSuccessfully - flag to indicate if submission was successful
	 */
	const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

	const [accountType, setAccountType] = useState(null);

	const officeAddressDefaultConfig = getFormTemplate(
		ADDRESS_FORM_TYPE.OFFICE
	);
	const [officeAddressFormState, setOfficeAddressFormState] = useState({
		officeAddress1: officeAddressDefaultConfig.street1,
		officeAddress2: officeAddressDefaultConfig.street2,
		officeAddress3: officeAddressDefaultConfig.street3,
		city: officeAddressDefaultConfig.city,
		state: officeAddressDefaultConfig.state,
		zipCode: officeAddressDefaultConfig.zipCode,
		country: officeAddressDefaultConfig.country,
	});

	// use this for passing up state of AddressForm component for when we do validation
	const homeAddressDefaultConfig = getFormTemplate(
		ADDRESS_FORM_TYPE.INTERNATIONAL
	);
	const [homeAddressFormState, setHomeAddressFormState] = useState({
		homeStreet: homeAddressDefaultConfig.street,
		homeCity: homeAddressDefaultConfig.city,
		homeState: homeAddressDefaultConfig.state,
		homeZipCode: homeAddressDefaultConfig.zipCode,
		homeCountry: homeAddressDefaultConfig.country,
	});

	// used to populate AddressForm component with values from API
	const [currentCountry, setCurrentCountry] = useState('default');
	const [currentHomeCountry, setCurrentHomeCountry] = useState('default');
	const [statesFromApi, setStatesFromApi] = useState({
		default: [{ key: '--select--', value: '--select--' }],
	});
	const [countriesFromApi, setCountriesFromApi] = useState([]);
	const [utAgencyPreloaded, setUtAgencyPreloaded] = useState(false);
	// eslint-disable-next-line no-unused-vars
	const [utCollegePreloaded, setUtCollegePreloaded] = useState(false);

	const queryForDepartmentNames = value => {
		let results = [];
		return new Promise((resolve, reject) => {
			axiosInstance
				.get('/api/UserAccessReview/QueryDepartmentNames/' + value)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								let data = response.data.payload;
								switch (responseCode) {
									case 200:
										results = data;
										break;
									default:
										results = [];
										break;
								}
							}
							break;
						default:
							results = [];
							break;
					}
					resolve(results);
				})
				.catch(errors => {
					reject(results);
				});
		});
	};

	const queryForBusinessUnits = value => {
		let results = [];
		return new Promise((resolve, reject) => {
			axiosInstance
				.get('/api/UserAccessReview/QueryBusinessUnits', {
					params: { businessUnit: value },
				})
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								let data = response.data.payload;
								switch (responseCode) {
									case 200:
										results = data;
										break;
									default:
										results = [];
										break;
								}
							}
							break;
						default:
							results = [];
							break;
					}
					resolve(results);
				})
				.catch(() => {
					reject(results);
				});
		});
	};

	const queryForCostCenters = value => {
		let results = [];
		return new Promise((resolve, reject) => {
			axiosInstance
				.get('/api/UserAccessReview/QueryCostCenters/' + value)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								let data = response.data.payload;
								switch (responseCode) {
									case 200:
										results = data;
										break;
									default:
										results = [];
										break;
								}
							}
							break;
						default:
							results = [];
							break;
					}
					resolve(results);
				})
				.catch(errors => {
					reject(results);
				});
		});
	};

	/**
	 * set(Form) - data for input form
	 */
	const [form, setForm] = useState({
		//Personnel Information
		jobTitle: {
			label: 'Job Title',
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					maxLength: 75,
				},
				touched: false,
			},
			settings: {
				readOnly: true,
				type: TextboxTypes.TEXT,
				displayFor: [
					UAR_CONSTANTS.EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
				],
			},
		},
		jobTitleNonEmployee: {
			label: 'Job Title',
			value: '',
			options: [],
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				type: DropdownTypes.DROPDOWN,
				displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
			},
		},
		businessUnit: {
			label: 'Business Unit',
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					serverSide: {
						route: '/api/UserAccessReview/Validate/BusinessUnit',
						key: 'BusinessUnit',
						includeEsoId: false,
					},
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.AUTOCOMPLETE,
				autocomplete: value => {
					return queryForBusinessUnits(value);
				},
				displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
			},
		},
		costCenter: {
			label: 'Cost Center',
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					maxLength: 16,
					serverSide: {
						route: '/api/UserAccessReview/Validate/CostCenter',
						key: 'costcenter',
						includeEsoId: false,
					},
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.AUTOCOMPLETE,
				autocomplete: value => {
					return queryForCostCenters(value);
				},
				displayFor: [
					UAR_CONSTANTS.NON_EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
				],
			},
		},
		costCenterEmployee: {
			label: 'Cost Center',
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					maxLength: 16,
					serverSide: {
						route: '/api/UserAccessReview/Validate/CostCenter',
						key: 'costcenter',
						includeEsoId: false,
					},
				},
				touched: false,
			},
			settings: {
				readOnly: true,
				type: TextboxTypes.TEXT,
				displayFor: [UAR_CONSTANTS.EMPLOYEE_KEY],
			},
		},
		departmentName: {
			label: 'Department',
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					maxLength: 75,
					regex: 'alphanumericspecial',
					serverSide: {
						route: '/api/UserAccessReview/Validate/DepartmentName',
						key: 'departmentname',
						includeEsoId: false,
					},
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.AUTOCOMPLETE,
				autocomplete: value => {
					return queryForDepartmentNames(value);
				},
				displayFor: [
					UAR_CONSTANTS.EMPLOYEE_KEY,
					UAR_CONSTANTS.NON_EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
				],
			},
		},
		supervisorEmail: {
			label: 'Manager Email',
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					maxLength: 75,
					regex: 'email',
					serverSide: {
						route: '/api/UserAccessReview/Validate/ManagerEmail',
						key: 'email',
						includeEsoId: false,
					},
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.TEXT,
				displayFor: [
					UAR_CONSTANTS.EMPLOYEE_KEY,
					UAR_CONSTANTS.NON_EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
				],
			},
		},
		hospitalAffiliation: {
			label: 'Hospital Affiliation',
			value: '',
			options: [],
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					invalidIndex: 0,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: DropdownTypes.DROPDOWN, //TODO|BPK - change this to TextboxTypes.SEARCH and provide a search function handler,
				displayFor: [
					UAR_CONSTANTS.EMPLOYEE_KEY,
					UAR_CONSTANTS.NON_EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
				],
			},
		},
		phoneNumber: {
			label: 'Phone Number',
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: false,
					maxLength: 20,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.TEXT,
				displayFor: [
					UAR_CONSTANTS.EMPLOYEE_KEY,
					UAR_CONSTANTS.NON_EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
				],
			},
		},
		accountCoordinatorEmail: {
			label: "Account Coordinator's Email",
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					maxLength: 75,
					regex: 'email',
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.TEXT,
				displayFor: [
					UAR_CONSTANTS.EMPLOYEE_KEY,
					UAR_CONSTANTS.NON_EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
				],
			},
		},
		//Personal Information
		alternateEmail: {
			label: 'Alternate Email Address',
			value: '',
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					regex: 'email',
					serverSide: {
						route: '/api/UserAccessReview/Validate/AlternateEmail',
						key: 'AlternateEmail',
						includeEsoId: true,
					},
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.TEXT,
				displayFor: [
					UAR_CONSTANTS.EMPLOYEE_KEY,
					UAR_CONSTANTS.NON_EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
				],
			},
		},
		//user type information
		userType: {
			label: 'User Type',
			value: '',
			options: [],
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: false,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: DropdownTypes.DROPDOWN,
				displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
			},
		},
		otherUserType: {
			label: 'Specify Other',
			value: '',
			options: [],
			visible: false,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					maxLength: 100,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.TEXT,
				displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
			},
		},
		researcherUserType: {
			label: 'Additional Information',
			value: '',
			options: [],
			visible: false,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
					maxLength: 100,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.TEXT,
				displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
			},
		},
		regulatoryUserTypeAgency: {
			label: 'Agency',
			value: '',
			options: [],
			visible: false,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: DropdownTypes.DROPDOWN,
				displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
			},
		},
		regulatoryUserTypeLocation: {
			label: 'Location',
			value: '',
			options: [],
			visible: false,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: DropdownTypes.DROPDOWN,
				displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
			},
		},
		studentUserTypeCollege: {
			label: 'College',
			value: '',
			options: [],
			visible: false,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: DropdownTypes.DROPDOWN,
				displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
			},
		},
	});

	/**
	 * watcher for populating User Type > Location with values when Agency options are available
	 */
	useEffect(() => {
		if (utAgencyPreloaded) {
			populateRegulatoryUserLocations(utAgencyPreloaded);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [utAgencyPreloaded]);

	//LIFECYCLE
	useEffect(() => {
		if (profileDetails) {
			switch (profileDetails.type) {
				case 'employee':
					setAccountType(UAR_CONSTANTS.EMPLOYEE_KEY);
					break;
				case 'nonEmployee':
					setAccountType(UAR_CONSTANTS.NON_EMPLOYEE_KEY);
					break;
				case 'contingentWorker':
					setAccountType(UAR_CONSTANTS.CONTINGENT_WORKER_KEY);
					break;
				default:
					setAccountType(UAR_CONSTANTS.EMPLOYEE_KEY);
			}

			const newForm = { ...form };
			let requestGetHospitals = axiosInstance.get(
				'/api/UserAccessReview/GetHospitals'
			);
			let requestGetCountries = axiosInstance.get(
				'/api/UserAccessReview/GetCountries'
			);
			let requestGetUserTypes = axiosInstance.get(
				'/api/UserAccessReview/GetNonEmployeeUserTypes'
			);
			let requestGetJobTitles = axiosInstance.get(
				'/api/UserAccessReview/GetNonEmployeeJobTitles'
			);
			axios
				.all([
					requestGetHospitals,
					requestGetCountries,
					requestGetUserTypes,
					requestGetJobTitles,
				])
				.then(
					axios.spread((...responses) => {
						const responseGetHospitals = responses[0];
						const responseGetCountries = responses[1];
						const responseGetUserTypes = responses[2];
						const responseGetJobTitles = responses[3];
						//hospitals
						switch (responseGetHospitals.status) {
							case 200:
								if (responseGetHospitals.data.responseCode) {
									const responseCode =
										responseGetHospitals.data.responseCode;
									let data =
										responseGetHospitals.data.payload;
									switch (responseCode) {
										case 200:
											newForm.hospitalAffiliation.options.push(
												{
													key: '--select--',
													value: '--select--',
												}
											);
											data.forEach(provider => {
												newForm.hospitalAffiliation.options.push(
													{
														key: provider,
														value: provider,
													}
												);
											});
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}
						//countries
						switch (responseGetCountries.status) {
							case 200:
								if (responseGetCountries.data.responseCode) {
									const responseCode =
										responseGetCountries.data.responseCode;
									let data =
										responseGetCountries.data.payload;
									switch (responseCode) {
										case 200:
											setCountriesFromApi(
												[
													{
														key: '--select--',
														value: '--select--',
													},
													...data.map(country => ({
														key: country.Key,
														value: country.Key,
													})),
												] || []
											);
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}
						//user types
						switch (responseGetUserTypes.status) {
							case 200:
								if (responseGetUserTypes.data.responseCode) {
									const responseCode =
										responseGetUserTypes.data.responseCode;
									let data =
										responseGetUserTypes.data.payload;
									switch (responseCode) {
										case 200:
											newForm.userType.options.push({
												key: '--select--',
												value: '--select--',
											});

											data.forEach(type => {
												newForm.userType.options.push({
													key: type.Key,
													value: type.Value,
												});
											});
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}
						//job titles
						switch (responseGetJobTitles.status) {
							case 200:
								if (responseGetJobTitles.data.responseCode) {
									const responseCode =
										responseGetJobTitles.data.responseCode;
									let data =
										responseGetJobTitles.data.payload;
									switch (responseCode) {
										case 200:
											newForm.jobTitleNonEmployee.options.push(
												{
													key: '--select--',
													value: '--select--',
												}
											);

											data.forEach(title => {
												newForm.jobTitleNonEmployee.options.push(
													{
														key: title.Value,
														value: title.Value,
													}
												);
											});
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						//reset form
						setForm(newForm);
						// validate();
					})
				)
				.catch(errors => {
					setError(true);
				});

			//set default countries based on profile
			if ('country' in profileDetails && profileDetails.country) {
				setCurrentCountry(profileDetails.country);
			}
			if ('homeCountry' in profileDetails && profileDetails.homeCountry) {
				setCurrentHomeCountry(profileDetails.homeCountry);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profileDetails]);

	/**
	 * when profile information becomes available, populate the form(s)
	 */
	useEffect(() => {
		if (profileDetails) {
			let newForm = { ...form };
			for (let key in newForm) {
				if (key in profileDetails) {
					newForm[key].value = profileDetails[key];
					if (profileDetails[key]) {
						switch (key) {
							case 'businessUnit':
								validateElement(key, newForm[key]);
								break;
							case 'costCenter':
								validateElement(key, newForm[key]);
								break;
							case 'userType':
								if (
									profileDetails[key].toLocaleLowerCase() ===
										'tenant badge only' ||
									profileDetails[key].toLocaleLowerCase() ===
										'regulatory user'
								) {
									newForm['alternateEmail'].visible = false;
								}
								break;
							case 'regulatoryUserTypeAgency':
								newForm[key].visible = true;
								populateRegulatoryUserAgencies(14);
								break;
							case 'regulatoryUserTypeLocation':
								newForm[key].visible = true;
								newForm[
									'regulatoryUserTypeAgency'
								].visible = true;
								populateRegulatoryUserAgencies(14);
								break;
							case 'studentUserTypeCollege':
								newForm[key].visible = true;
								populateStudentColleges();
								break;
							case 'researcherUserType':
							case 'otherUserType':
								newForm[key].visible = true;
								break;
							default:
								break;
						}
					}
				} else if (key === 'jobTitleNonEmployee') {
					newForm.jobTitleNonEmployee.value = profileDetails.jobTitle;
				} else if (key === 'costCenterEmployee') {
					newForm.costCenterEmployee.value =
						profileDetails.costCenter;
				}
			}
			setForm(newForm);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profileDetails]);

	/**
	 * "hide" form entry if it isn't supposed to be displayed at all to aid in validation
	 */
	useEffect(() => {
		if (
			accountType &&
			(accountType === UAR_CONSTANTS.EMPLOYEE_KEY ||
				accountType === UAR_CONSTANTS.NON_EMPLOYEE_KEY ||
				accountType === UAR_CONSTANTS.CONTINGENT_WORKER_KEY)
		) {
			const newForm = { ...form };
			Object.keys(newForm).forEach(key => {
				if (!newForm[key].settings.displayFor.includes(accountType)) {
					const newForm = { ...form };
					newForm[key].visible = false;
				}
			});
			setForm(newForm);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountType]);

	/**
	 * Query API for more state lists when country value is updated (Office and Home Address)
	 */
	useEffect(() => {
		updateStatesList([currentCountry, currentHomeCountry]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentCountry, currentHomeCountry]);

	//FUNCTIONS
	/**
	 * event handler for form input changes
	 * @param {ChangeEvent<HTMLInputElement>} event
	 * @param {string} key
	 */
	const handleChange = (event, key) => {
		if (key in form) {
			let newForm = { ...form };
			newForm[key].value = event.target.value;

			//reset all if appropriate
			if (
				[
					'userType',
					'regulatoryUserTypeAgency',
					'studentUserTypeCollege',
				].includes(key)
			) {
				newForm.otherUserType.visible = false;
				newForm.regulatoryUserTypeAgency.visible = false;
				newForm.researcherUserType.visible = false;
				newForm.studentUserTypeCollege.visible = false;
				newForm.regulatoryUserTypeLocation.visible = false;
				newForm.studentUserTypeCollege.visible = false;
			}
			//update visibility
			switch (key) {
				case 'userType':
					newForm.alternateEmail.visible = true;
					switch (newForm[key].value.toLocaleLowerCase()) {
						//other - make textbox visible
						case 'other':
							newForm.otherUserType.visible = true;
							break;
						//regulatory user - make agency visible, query population
						case 'regulatory user':
							populateRegulatoryUserAgencies(14);
							newForm.regulatoryUserTypeAgency.visible = true;
							newForm.alternateEmail.visible = false;
							newForm.alternateEmail.value = '';
							break;
						//researcher - make textbox visible
						case 'researcher':
							newForm.researcherUserType.visible = true;
							break;
						//student - make dropdown visible
						case 'student':
							populateStudentColleges();
							newForm.studentUserTypeCollege.visible = true;
							break;
						case 'tenant badge only':
							newForm.alternateEmail.visible = false;
							newForm.alternateEmail.value = '';
							break;
						default:
							break;
					}
					break;
				case 'regulatoryUserTypeAgency':
					//populate locations and display
					populateRegulatoryUserLocations(
						newForm.regulatoryUserTypeAgency.value
					);
					newForm.regulatoryUserTypeAgency.visible = true;
					newForm.regulatoryUserTypeLocation.visible = true;
					break;
				case 'studentUserTypeCollege':
					newForm.studentUserTypeCollege.visible = true;
					break;
				default:
					break;
			}

			setForm(newForm);
		}
	};

	const populateRegulatoryUserAgencies = id => {
		axiosInstance
			.get('/api/UserAccessReview/GetNonEmployeeUserSubtypes/' + id)
			.then(response => {
				switch (response.status) {
					case 200:
						if (response.data.responseCode) {
							const responseCode = response.data.responseCode;
							let data = response.data.payload;
							switch (responseCode) {
								case 200:
									if (data.length > 0) {
										const newForm = { ...form };
										newForm.regulatoryUserTypeAgency.options =
											[];
										newForm.regulatoryUserTypeAgency.options.push(
											{
												key: '--select--',
												value: '--select--',
											}
										);

										data.forEach(opt => {
											newForm.regulatoryUserTypeAgency.options.push(
												{
													key: opt.Key,
													value: opt.Value,
												}
											);
										});
										newForm.regulatoryUserTypeAgency.options.push();
										newForm.regulatoryUserTypeAgency.visible = true;
										setForm(newForm);
										setUtAgencyPreloaded(
											newForm.regulatoryUserTypeAgency
												.value
										);
									} else {
										const newForm = { ...form };
										newForm.regulatoryUserTypeAgency.visible = false;
										setForm(newForm);
									}
									break;
								default:
									break;
							}
						}
						break;
					default:
						break;
				}
			})
			.catch(errors => {});
	};

	const populateRegulatoryUserLocations = value => {
		const selection = form.regulatoryUserTypeAgency.options.filter(
			opt => opt.value === value
		);
		if (selection && selection.length > 0) {
			const id = selection[0].key;
			axiosInstance
				.get('/api/UserAccessReview/GetNonEmployeeUserSubtypes/' + id)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								let data = response.data.payload;
								switch (responseCode) {
									case 200:
										if (data.length > 0) {
											const newForm = { ...form };
											newForm.regulatoryUserTypeLocation.options =
												[];
											newForm.regulatoryUserTypeLocation.options.push(
												{
													key: '--select--',
													value: '--select--',
												}
											);

											data.forEach(opt => {
												newForm.regulatoryUserTypeLocation.options.push(
													{
														key: opt.Key,
														value: opt.Value,
													}
												);
											});
											newForm.regulatoryUserTypeLocation.options.push();
											newForm.regulatoryUserTypeLocation.visible = true;
											setForm(newForm);
										} else {
											const newForm = { ...form };
											newForm.regulatoryUserTypeLocation.visible = false;
											setForm(newForm);
										}
										break;
									default:
										break;
								}
							}
							break;
						default:
							break;
					}
				})
				.catch(errors => {});
		}
	};

	const populateStudentColleges = () => {
		axiosInstance
			.get('/api/UserAccessReview/GetStudentColleges')
			.then(response => {
				switch (response.status) {
					case 200:
						if (response.data.responseCode) {
							const responseCode = response.data.responseCode;
							let data = response.data.payload;
							switch (responseCode) {
								case 200:
									if (data.length > 0) {
										const newForm = { ...form };
										newForm.studentUserTypeCollege.options =
											[];
										newForm.studentUserTypeCollege.options.push(
											{
												key: '--select--',
												value: '--select--',
											}
										);

										data.forEach(opt => {
											newForm.studentUserTypeCollege.options.push(
												{ key: opt, value: opt }
											);
										});
										newForm.studentUserTypeCollege.options.push();
										newForm.studentUserTypeCollege.visible = true;
										setForm(newForm);
										setUtCollegePreloaded(
											newForm.studentUserTypeCollege.value
										);
									} else {
										const newForm = { ...form };
										newForm.studentUserTypeCollege.visible = false;
										setForm(newForm);
									}
									break;
								default:
									break;
							}
						}
						break;
					default:
						break;
				}
			})
			.catch(errors => {});
	};

	/**
	 * actions to take upon form submission
	 */
	const submitHandler = async () => {
		const isValid = await validate();
		if (isValid) {
			//translate the form data values back to the original profile
			let newProfileDetails = { ...profileDetails };
			let newForm = { ...form };
			newForm = {
				...newForm,
				...homeAddressFormState,
				...officeAddressFormState,
			};
			for (let key in newForm) {
				if (key in newProfileDetails) {
					if (
						key === 'jobTitle' &&
						accountType === UAR_CONSTANTS.NON_EMPLOYEE_KEY
					) {
						newProfileDetails.jobTitle =
							newForm.jobTitleNonEmployee.value;
					} else if (
						key === 'costCenterEmployee' &&
						accountType === UAR_CONSTANTS.EMPLOYEE
					) {
						newProfileDetails.costCenter =
							newForm.costCenterEmployee.value;
					} else {
						if (newForm[key].visible) {
							newProfileDetails[key] = newForm[key].value;
						}
					}
				}
			}

			const payload = {
				loggedInUser: loggedInUser.loggedInUser,
				targetUser: newProfileDetails,
			};

			setSubmitted(true);
			axiosInstance
				.post(
					'/api/UserAccessReview/UpdateUserInfo',
					WrapImsPacket(payload)
				)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								switch (responseCode) {
									case 200:
										setSubmittedSuccessfully(true);
										break;
									default:
										setSubmitted(false);
										setSubmittedSuccessfully(false);
										break;
								}
							}
							break;

						default:
							setSubmitted(false);
							setSubmittedSuccessfully(false);
							break;
					}
				})
				.catch(errors => {
					setSubmitted(false);
					setSubmittedSuccessfully(false);
				});
		} else {
			dispatch(actions.clearMessages());
			dispatch(
				actions.addMessage({
					Key: 2,
					Value: 'Please correct the validation issues as indicated below.',
				})
			);
		}
		//TODO|BPK: else post message
	};

	/**
	 * actions to take if cancelled
	 */
	const cancelHandler = () => {
		props.history.goBack();
	};

	const isDefaultValueFromDropdown = value => {
		return value === '--select--' || value === null;
	};

	/**
	 * Invokes an element's configured server-side validation. Useful for individual validations on render.
	 * @param {string} formKey key of form element to validate.
	 * @param {object} formEl form element object.
	 * @returns {void}
	 */
	const validateElement = (formKey, formEl) => {
		let newForm = { ...form };

		if (formEl?.validation?.rules?.serverSide) {
			formEl.validation.touched = true;
			const payload = {
				[formEl.validation.rules.serverSide.key]: formEl.value,
			};
			const payloadExtras = formEl.validation.rules.serverSide
				.includeEsoId
				? { EsoFiId: esoFiId }
				: {};
			axiosInstance
				.post(
					formEl.validation.rules.serverSide.route,
					WrapImsPacket({
						...payload,
						...payloadExtras,
					})
				)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								let data = response.data.payload;
								switch (responseCode) {
									case 200:
										newForm[formKey].validation.valid =
											data.valid;
										newForm[formKey].validation.message =
											data.message;

										setForm(newForm);
										break;
									default:
										break;
								}
							}
							break;

						default:
							break;
					}
				});
		}
	};

	/**
	 * form validation logic
	 * @param {ChangeEvent<HTMLInputElement>} event
	 */
	const validate = async event => {
		const touchedKey =
			event && event.target && event.target.id ? event.target.id : null;
		let newForm = { ...form };
		newForm = {
			...newForm,
			...homeAddressFormState,
			...officeAddressFormState,
		};
		let allValid = true;
		for (let key in newForm) {
			let valid = false;
			let value = newForm[key].value;
			let visible = newForm[key].visible;
			let message = '';
			let contains = [];
			const rules = newForm[key].validation.rules;

			if (visible) {
				switch (key) {
					case 'hospitalAffiliation':
					case 'userType':
					case 'regulatoryUserTypeAgency':
					case 'regulatoryUserTypeLocation':
					case 'studentUserTypeCollege':
						//  dropdown; value needs to be in the options
						if (isDefaultValueFromDropdown(value)) {
							valid = !newForm[key].validation.rules.required;
						} else {
							contains = newForm[key].options.filter(option => {
								return option.value === value;
							});
							valid = contains.length === 1;
						}
						message = !valid
							? 'Please select a valid option.'
							: message;
						break;
					case 'country':
					case 'homeCountry':
						let countryOptions = countriesFromApi;
						valid = isDefaultValueFromDropdown(value)
							? !newForm[key].validation.rules.required
							: countryOptions.some(
									option => option.key === value
							  );
						if (!valid) {
							message = 'Please select a valid country.';
						}
						break;
					case 'state':
					case 'homeState':
						let stateOptions = [];
						if (key === 'state') {
							stateOptions = statesFromApi[currentCountry];
						} else if (key === 'homeState') {
							stateOptions = statesFromApi[currentHomeCountry];
						}
						valid = isDefaultValueFromDropdown(value)
							? !newForm[key].validation.rules.required
							: stateOptions.some(option => option.key === value);
						if (!valid) {
							message = 'Please select a valid state/province.';
						}
						break;
					case 'zipCode':
					case 'homeZipCode':
						const countryForZipValidation =
							key === 'zipCode'
								? newForm.country.value
								: newForm.homeCountry.value;

						const isCountryUSOrDefault =
							isSelectedCountryUSOrDefault(
								countryForZipValidation
							);
						valid = isCountryUSOrDefault
							? isValidZip(value)
							: isValidCountryCode(value);

						if (!valid) {
							message = isCountryUSOrDefault
								? 'Please supply a valid zip code'
								: 'Please supply a valid country code';
						}
						break;
					case 'phoneNumber':
						valid = value ? utils.isValidPhoneNumber(value) : true;
						if (!valid) {
							message = 'Please supply a valid phone number';
						}
						break;
					default:
						//check for required, length, etc.
						valid = true;
						if (valid && rules.maxLength) {
							if (value && value.length > rules.maxLength) {
								valid = valid && false;
								message = `The value provided is too long.  ${value.length} of a possible ${rules.maxLength} characters used.`;
							}
						}

						if (valid && rules.required) {
							if (!value) {
								valid = valid && false;
								message = 'This field is required.';
							}
						}

						if (valid && rules.regex && (value || rules.required)) {
							const pattern = /(?=[|&])/;
							let tokens = rules.regex.split(pattern);
							let operation = 'OR';
							let regexValid = true;
							let regexMessage = '';

							for (let i = 0; i < tokens.length; i++) {
								if (tokens[i].startsWith('|')) {
									operation = 'OR';
								} else {
									operation = 'AND';
								}

								switch (rules.regex) {
									case 'email':
										if (!utils.isValidEmail(value)) {
											regexValid = false;
											regexMessage =
												'The value provided is not a valid email address.';
										}
										break;
									case 'alphanumericspecial':
										const alphaNumericSpecialPattern =
											/^[\w\s!@;#$%^&*()_\-+~`<>?./|[\]{}\\,]*$/;
										if (
											!utils.matchPattern(
												value,
												alphaNumericSpecialPattern
											)
										) {
											regexValid = false;
											regexMessage =
												'The value provided contains invalid characters.';
										}
										break;
									default:
										break;
								}

								switch (operation) {
									case 'AND':
										valid = valid && regexValid;
										break;

									default:
										valid = valid || regexValid;
										break;
								}
							}

							if (!valid) {
								message = regexMessage;
							}
						}

						if (
							valid &&
							rules.serverSide &&
							(value || rules.required)
						) {
							const payload = {
								[rules.serverSide.key]: value,
							};
							const payloadExtras = rules.serverSide.includeEsoId
								? { EsoFiId: esoFiId }
								: {};

							await axiosInstance
								.post(
									rules.serverSide.route,
									WrapImsPacket({
										...payload,
										...payloadExtras,
									})
								)
								.then(response => {
									switch (response.status) {
										case 200:
											if (response.data.responseCode) {
												const responseCode =
													response.data.responseCode;
												let data =
													response.data.payload;
												switch (responseCode) {
													case 200:
														valid =
															valid && data.valid;
														message = data.message;
														newForm[
															key
														].validation.valid =
															valid;
														newForm[
															key
														].validation.message =
															message;
														break;
													default:
														break;
												}
											}
											break;

										default:
											break;
									}
								})
								.catch(errors => {});
						}
				}
			} else {
				valid = true;
			}
			newForm[key].validation.valid = valid;
			newForm[key].validation.message = message;
			newForm[key].validation.touched = true;

			//no `touchedKey` indicates a submission event
			if (!touchedKey) {
				newForm[key].validation.touched = true;
			}

			//exempt if invisible
			valid = newForm[key].visible ? valid : true;
			allValid = allValid && valid;
		}

		//presence of `touchedKey` indicates an input focus-change event
		if (touchedKey) {
			newForm[touchedKey].validation.touched = true;
		}

		setForm(newForm);

		return allValid;
	};

	//RENDER
	/**
	 * Retrieve settings from state and generate the input component
	 * @param {string} key
	 */
	const generateFormInput = (key, type) => {
		let formInput = null;
		if (
			key in form &&
			form[key].visible &&
			form[key].settings.displayFor.includes(accountType)
		) {
			formInput = (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					handleChange={e => handleChange(e, key)}
					validateChange={validate}
					inputType={
						form[key].settings && form[key].settings.type
							? form[key].settings.type
							: 'text'
					}
					readOnly={
						(form[key].settings && form[key].settings.readOnly) ||
						submittedSuccessfully
					}
				/>
			);
		}
		return formInput;
	};

	const updateOfficeAddressFormState = addressForm => {
		let newAddressFormState = { ...officeAddressFormState };
		for (const key in addressForm) {
			const mappedName = (() => {
				switch (key) {
					case 'street1':
						return 'officeAddress1';
					case 'street2':
						return 'officeAddress2';
					case 'street3':
						return 'officeAddress3';
					default:
						// this is different than the others because the names match up and
						// don't need to be altered as much
						return key;
				}
			})();

			newAddressFormState[mappedName] = {
				...newAddressFormState[mappedName],
				...addressForm[key],
			};
		}
		setOfficeAddressFormState(newAddressFormState);

		//update country
		setCurrentCountry(addressForm.country.value);
	};

	/**
	 * Given a list of countries, query for their states/provinces to store for quick lookup
	 * @param {string[]} countryNames list of country names to query for
	 */
	const updateStatesList = countryNames => {
		//remove if already found in state variable
		const filteredCountryNames = countryNames.filter(
			country => !Object.keys(statesFromApi).includes(country)
		);
		//remove duplicates
		const uniqueCountryNames = [...new Set(filteredCountryNames)];

		//call API to get new states
		const apiRequests = [];
		uniqueCountryNames.forEach(country => {
			apiRequests.push(
				axiosInstance.get('/api/UserAccessReview/GetStates/' + country)
			);
		});

		//copy state variable
		const statesCopy = { ...statesFromApi };

		//get states for all countries
		axios
			.all(apiRequests)
			.then(
				axios.spread((...responses) => {
					responses.forEach(response => {
						if (response.data.responseCode) {
							const responseCode = response.data.responseCode;
							let data = response.data.payload;
							switch (responseCode) {
								case 200:
									const countryKey = response.config.url
										.split('/')
										.at(-1);
									statesCopy[countryKey] = [
										{
											key: '--select--',
											value: '--select--',
										},
										...data.map(state => ({
											key: state.Value,
											value: state.Value,
										})),
									];
									break;
								default:
									break;
							}
						}
					});
				})
			)
			.catch(() => {})
			.finally(() => {
				//update state variable
				setStatesFromApi(statesCopy);
			});
	};

	const updateHomeAddressFormState = addressForm => {
		let newAddressFormState = { ...homeAddressFormState };
		for (const key in addressForm) {
			const mappedName = (() => {
				switch (key) {
					case 'street':
						return 'homeStreet';
					case 'city':
						return 'homeCity';
					case 'state':
						return 'homeState';
					case 'zipCode':
						return 'homeZipCode';
					case 'country':
						return 'homeCountry';
					default:
						return '';
				}
			})();

			newAddressFormState[mappedName] = {
				...newAddressFormState[mappedName],
				...addressForm[key],
			};
		}
		setHomeAddressFormState(newAddressFormState);
		//set home country
		setCurrentHomeCountry(newAddressFormState.homeCountry.value);
	};

	/**
	 * Personnel form components
	 */
	const formOutputPersonnel = () => {
		let defaultAddressValues = {
			officeAddress1: '',
			officeAddress2: '',
			officeAddress3: '',
			city: '',
			state: null,
			zipCode: '',
			country: null,
		};

		for (let key in defaultAddressValues) {
			if (key in profileDetails) {
				defaultAddressValues[key] = profileDetails[key];
			}

			// if form has been submitted, use the values that they already entered
			if (submittedSuccessfully && key in form) {
				defaultAddressValues[key] = form[key].value;
			}
		}

		const displayForTypes = [
			UAR_CONSTANTS.EMPLOYEE_KEY,
			UAR_CONSTANTS.NON_EMPLOYEE_KEY,
			UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
		];
		const shouldShowForm = displayForTypes.some(
			allowedAccountType => allowedAccountType === accountType
		);

		return (
			<>
				{/* Job Title */}
				{generateFormInput('jobTitle')}
				{generateFormInput('jobTitleNonEmployee')}
				{/* Business Unit */}
				{generateFormInput('businessUnit')}
				{/* Cost Center */}
				{generateFormInput('costCenter')}
				{generateFormInput('costCenterEmployee')}
				{/* Department Name */}
				{generateFormInput('departmentName')}
				{/* Manager Email */}
				{generateFormInput('supervisorEmail')}
				{/* Hospital Affiliation */}
				{generateFormInput('hospitalAffiliation')}
				{shouldShowForm && (
					<AddressForm
						defaultValues={{
							street1: defaultAddressValues.officeAddress1 || '',
							street2: defaultAddressValues.officeAddress2 || '',
							street3: defaultAddressValues.officeAddress3 || '',
							city: defaultAddressValues.city || '',
							state: defaultAddressValues.state || null,
							zipCode: defaultAddressValues.zipCode || '',
							country: defaultAddressValues.country || null,
						}}
						formType={ADDRESS_FORM_TYPE.OFFICE}
						onUpdate={updateOfficeAddressFormState}
						onValidate={validate}
						options={{
							country: countriesFromApi || [],
							state: statesFromApi[currentCountry] || [],
						}}
						shouldFieldsBeReadOnly={submittedSuccessfully}
					/>
				)}
				{/* Phone Number */}
				{generateFormInput('phoneNumber')}
				{/* Account Coordinator's Email */}
				{generateFormInput('accountCoordinatorEmail')}
				{generateFormInput('alternateEmail')}
			</>
		);
	};

	/**
	 * Home Address form components
	 */
	const formOutputHome = () => {
		let defaultValues = {
			homeStreet: '',
			homeCity: '',
			homeState: null,
			homeZipCode: '',
			homeCountry: null,
		};

		for (let key in defaultValues) {
			if (key in profileDetails) {
				defaultValues[key] = profileDetails[key];
			}
		}

		const displayForTypes = [UAR_CONSTANTS.NON_EMPLOYEE_KEY];
		const shouldShowForm = displayForTypes.some(
			allowedAccountType => allowedAccountType === accountType
		);

		if (!shouldShowForm) {
			return null;
		}

		return (
			<AddressForm
				defaultValues={{
					street: defaultValues.homeStreet || '',
					city: defaultValues.homeCity || '',
					state: defaultValues.homeState || null,
					zipCode: defaultValues.homeZipCode || '',
					country: defaultValues.homeCountry || null,
				}}
				formType={ADDRESS_FORM_TYPE.INTERNATIONAL}
				onUpdate={updateHomeAddressFormState}
				onValidate={validate}
				options={{
					country: countriesFromApi || [],
					state: statesFromApi[currentHomeCountry] || [],
				}}
				shouldFieldsBeReadOnly={submittedSuccessfully}
			/>
		);
	};
	/**
	 * Home Address form components
	 */
	const formOutputUserType = (
		<>
			{/* User Type */}
			{generateFormInput('userType')}
			{/* User subtype - Researcher */}
			{generateFormInput('researcherUserType')}
			{/* User Subtype - regulatory user*/}
			{generateFormInput('regulatoryUserTypeAgency')}
			{generateFormInput('regulatoryUserTypeLocation')}
			{/* User Subtype - student*/}
			{generateFormInput('studentUserTypeCollege')}
			{/* User subtype - other */}
			{generateFormInput('otherUserType')}
		</>
	);

	const disclaimer = () => {
		return (
			<div>
				<Headline6>Please Note:</Headline6>
				<p>
					Changes to <em>Job Title</em>, <em>Cost Center</em>,{' '}
					<em>Department</em>, <em>Manager's Email</em>, and{' '}
					<em>Address</em> for <strong>UPMC Employees</strong> and{' '}
					<strong>Contingent Workers</strong> will be temporary. These
					modifications will be overwritten by HR Direct on a nightly
					basis. All permanent changes to these values will need to be
					changed through Human Resources.
				</p>
			</div>
		);
	};

	/**
	 * return JSX to render
	 */
	return (
		<UARLayout
			title='Update User Information'
			loading={loading}
			error={error}
			errorMessage={errorMessage}
			canPerformActions={canPerformActions}
		>
			{submittedSuccessfully ? (
				<SubmissionSummary profile={profileDetails}>
					{disclaimer()}
					{!!profileDetails && formOutputPersonnel()}
				</SubmissionSummary>
			) : (
				<Edit
					cancellable={{
						handler: cancelHandler,
						disabled: submitted,
					}}
					submittable={{
						handler: submitHandler,
						label: 'Submit',
						disabled: submitted,
					}}
					loading={{
						isLoading: submitted,
						title: SUBMISSION_LOADING_TITLE,
						message: null,
					}}
					profile={profileDetails}
				>
					{disclaimer()}
					<Headline5>Personnel Information</Headline5>
					{!!profileDetails && formOutputPersonnel()}

					{accountType === UAR_CONSTANTS.NON_EMPLOYEE_KEY ? (
						<Headline5>Home Address</Headline5>
					) : null}
					{!!profileDetails && formOutputHome()}

					{accountType === UAR_CONSTANTS.NON_EMPLOYEE_KEY ? (
						<Headline5>Change User Type</Headline5>
					) : null}
					{formOutputUserType}
				</Edit>
			)}
		</UARLayout>
	);
};

export default UpdateProfile;
